// Which admin-panel routes this surface is allowed to report on (GIS-215).
//
// The panel serves two populations through one shell: hosts, and super-admin
// operators. Only the host-facing half is instrumented. That is not squeamishness
// about operator privacy — it is the same rule decision 4 already set for
// `otp_requested` on this surface: operator activity is not product usage, and
// the platform's highest-frequency users would dominate any metric they appear
// in. A "which panel screens get used" number that counts an operator sweeping
// /issues forty times a day tells you about the operator, not the product.
//
// So the allowlist below is a positive list, not a blocklist. A route added to
// App.js tomorrow reports nothing until somebody names it here, which is the
// safe default for a surface where the next route added could be another
// /users — see BLOCKED_PATH_PATTERNS in sanitize.js for what that costs.
//
// Keep in sync with App.js. The super-admin routes are listed in the comment at
// the bottom rather than in code, deliberately: an explicit deny list invites
// "add it to both" when the real answer is "add it to neither".

/**
 * Host-facing routes, as [pattern, matcher] pairs. `pattern` is what gets
 * reported as the `route` property — a stable, low-cardinality label that can
 * be grouped on without every event edit becoming its own row.
 *
 * Order matters: the first match wins, so literal paths precede parameterized
 * ones. `/events/new` has to beat `/events/:eventID/edit`-style matching, and
 * `/events/hosts` (super-admin) must not be swallowed by a loose `/events/*`.
 */
const HOST_ROUTES = [
  ["/", (p) => p === "/"],
  ["/profile-setup", (p) => p === "/profile-setup"],
  ["/home", (p) => p === "/home"],
  ["/settings", (p) => p === "/settings"],
  ["/users", (p) => p === "/users"],
  ["/events", (p) => p === "/events"],
  ["/events/completed", (p) => p === "/events/completed"],
  ["/events/cancelled", (p) => p === "/events/cancelled"],
  ["/events/suggestions", (p) => p === "/events/suggestions"],
  ["/events/new", (p) => p === "/events/new"],
  // The only parameterized host route. An event id in a URL is not a person
  // identifier, so it is safe to visit — but it is still collapsed to the
  // pattern for the `route` property, and the raw path rides along in
  // $current_url where PostHog expects it.
  ["/events/:eventID/edit", (p) => /^\/events\/[^/]+\/edit$/.test(p)],
];

/**
 * The route pattern for a pathname, or null if this is not a host-facing route.
 *
 * A null return is the signal not to report anything about this navigation. It
 * covers every super-admin route by omission: /kpis, /coupons, /payouts, /regions,
 * /host-applications, /host-nudges, /hosts, /hosts/:vendorID/edit, /events/hosts,
 * /events/requests, /admin-agent, /conversations, /gist-ai, /gist-ai-launcher,
 * /arbitration, /issues, /feedback, and the /payout-config redirect.
 *
 * @param {string} pathname
 * @returns {string|null}
 */
export function matchHostRoute(pathname) {
  if (typeof pathname !== "string" || !pathname) return null;
  // Trailing slashes are equivalent for routing but would miss every exact
  // comparison above. Normalize rather than doubling the table.
  const path = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.replace(/\/+$/, "")
    : pathname;
  const hit = HOST_ROUTES.find(([, matches]) => matches(path));
  return hit ? hit[0] : null;
}

export const _internal = { HOST_ROUTES };
